import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import { useSavedPreferences } from './useSavedPreferences';
import { useUserPreferences } from './useUserPreferences';

export const useViewFilters = ({
  storageKey,
  defaultFields,
  urlMapping,
  debounceDelay = 1000,
}) => {
  const {
    loadSavedPreferences,
    savePreferences,
    unsavePreferences,
    hasSavedPreferences,
    enabledFields,
    isPreferenceEnabled,
  } = useSavedPreferences(storageKey, defaultFields);

  const [singleValueFilters, setSingleValueFilters] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    
    Object.entries(urlMapping).forEach(([field, urlParam]) => {
      const paramValue = urlParams.get(urlParam);
      urlFilters[field] = paramValue ? paramValue.split(',') : [];
    });
    
    const hasUrlFilters = Object.values(urlFilters).some(arr => arr.length > 0);
    return hasUrlFilters ? urlFilters : loadSavedPreferences();
  });

  const debouncedSingleValueFilters = useDebounce(singleValueFilters, debounceDelay);

  const [, setSearchParams] = useSearchParams();

  const updateUrlParams = useCallback((filterValues) => {
    setSearchParams((prevParams) => {
      const urlParams = new URLSearchParams(prevParams);
      
      Object.entries(filterValues).forEach(([field, value]) => {
        const urlParam = urlMapping[field] || field;
        if (Array.isArray(value) && value.length > 0) {
          urlParams.set(urlParam, value.join(','));
        } else {
          urlParams.delete(urlParam);
        }
      });
      
      return urlParams;
    }, { replace: true });
  }, [urlMapping, setSearchParams]);

  const handleSingleFilterChange = useCallback((field, value) => {
    setSingleValueFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      updateUrlParams(updated);
      return updated;
    });
  }, [updateUrlParams]);

  const { savePreferencesToApi } = useUserPreferences();

  const handleSavePreferences = useCallback(async () => {
    const isSaved = hasSavedPreferences();
    if (isSaved) {
      const ok = unsavePreferences();
      if (ok) {
        await savePreferencesToApi({ [storageKey]: null });
        return { saved: false, message: 'Filters unsaved for this page.' };
      }
      return { saved: true, message: 'Could not unsave filters.' };
    } else {
      const ok = savePreferences(singleValueFilters);
      if (ok) {
        await savePreferencesToApi({ [storageKey]: singleValueFilters });
        return { saved: true, message: 'Filters saved — they will reload automatically next visit.' };
      }
      return { saved: false, message: 'Could not save filters.' };
    }
  }, [storageKey, singleValueFilters, savePreferences, unsavePreferences, hasSavedPreferences, savePreferencesToApi]);

  const resetFilters = useCallback(() => {
    setSingleValueFilters(defaultFields);
    updateUrlParams(defaultFields);
  }, [defaultFields, updateUrlParams]);

  useEffect(() => {
    const handleClearAll = () => {
      setSingleValueFilters(defaultFields);
      updateUrlParams(defaultFields);
    };
    window.addEventListener('clearAllFilters', handleClearAll);
    return () => window.removeEventListener('clearAllFilters', handleClearAll);
  }, [defaultFields, updateUrlParams]);

  const getOtherFilters = useCallback((excludeField) => {
    const other = { ...debouncedSingleValueFilters };
    delete other[excludeField];
    return other;
  }, [debouncedSingleValueFilters]);

  const mergeField = useCallback((singleValue, debouncedValue) => {
    if (singleValue?.length > 0) {
      return singleValue;
    }
    return debouncedValue || [];
  }, []);

  return {
    singleValueFilters,
    setSingleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
    updateUrlParams,
    resetFilters,
    enabledFields,
    isPreferenceEnabled,
  };
};

export const usePaginationReset = (mergedFilters, dateType, setPagination) => {
  const prevFiltersRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevFiltersRef.current = JSON.stringify(mergedFilters) + dateType;
      return;
    }

    const currentFiltersKey = JSON.stringify(mergedFilters) + dateType;
    
    if (prevFiltersRef.current !== currentFiltersKey) {
      prevFiltersRef.current = currentFiltersKey;
      setPagination(prev => {
        if (prev.pageIndex !== 0) {
          return { ...prev, pageIndex: 0 };
        }
        return prev;
      });
    }
  }, [mergedFilters, dateType, setPagination]);
};

